import { useEffect, useState, useRef } from "react";
import { useTutorial, STEPS } from "@/context/TutorialContext";
import { X, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const PAD = 10; // px padding around the spotlight target

function getVpSize() {
  return { w: window.innerWidth, h: window.innerHeight };
}

export function TutorialOverlay() {
  const { step, active, next, skip, runAction, waiting, stopWaiting } = useTutorial();
  const [targetRect, setTargetRect] = useState(null);
  const [vpSize, setVpSize] = useState(getVpSize());
  const pollRef = useRef(null);
  const waitPollRef = useRef(null);

  const currentStep = active && step >= 0 ? STEPS[step] : null;

  // Track viewport resizes
  useEffect(() => {
    const onResize = () => setVpSize(getVpSize());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Find the target element (with polling for lazily-rendered DOM nodes)
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    setTargetRect(null);

    if (!currentStep || currentStep.type === "modal" || !currentStep.selector) return;

    const tryFind = () => {
      const el = document.querySelector(currentStep.selector);
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return; // not visible yet
      setTargetRect({ left: r.left, top: r.top, width: r.width, height: r.height });
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      clearInterval(pollRef.current);
    };

    tryFind();
    pollRef.current = setInterval(tryFind, 150);
    return () => clearInterval(pollRef.current);
  }, [step]); // eslint-disable-line

  // Re-measure rect on scroll (keeps spotlight locked to target)
  useEffect(() => {
    if (!currentStep || currentStep.type === "modal" || !currentStep.selector) return;
    const onScroll = () => {
      const el = document.querySelector(currentStep.selector);
      if (!el) return;
      const r = el.getBoundingClientRect();
      setTargetRect({ left: r.left, top: r.top, width: r.width, height: r.height });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [step]); // eslint-disable-line

  // Poll for waitFor element after generate is clicked
  useEffect(() => {
    if (waitPollRef.current) clearInterval(waitPollRef.current);
    if (!waiting || !currentStep?.waitFor) return;

    waitPollRef.current = setInterval(() => {
      const el = document.querySelector(currentStep.waitFor);
      if (el) {
        clearInterval(waitPollRef.current);
        stopWaiting();
      }
    }, 400);
    return () => clearInterval(waitPollRef.current);
  }, [waiting, step]); // eslint-disable-line

  if (!active || !currentStep) return null;

  // ── "Waiting for generation" panel ───────────────────────────────────────
  if (waiting && currentStep.waitTitle) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-end justify-center pb-24 pointer-events-none">
        <div className="bg-[#0F1015]/95 border border-primary/30 rounded-2xl px-6 py-4 shadow-2xl backdrop-blur-md flex items-center gap-4 mx-4 max-w-sm w-full">
          <div className="shrink-0 h-8 w-8 rounded-full bg-primary/15 flex items-center justify-center">
            <Loader2 className="h-4 w-4 text-primary animate-spin" />
          </div>
          <div>
            <p className="font-heading font-black text-sm tracking-tight">{currentStep.waitTitle}</p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{currentStep.waitDesc}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Modal steps (welcome / done) ─────────────────────────────────────────
  if (currentStep.type === "modal") {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <div
          className="bg-[#0F1015] border border-white/10 rounded-2xl p-8 max-w-md mx-4 shadow-2xl"
          style={{ animation: "tutFadeUp 0.25s ease both" }}
        >
          <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight mb-3">
            {currentStep.title}
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-7">{currentStep.desc}</p>
          <div className="flex gap-3">
            <Button
              className="flex-1 bg-primary text-black hover:bg-primary/90 font-bold h-11"
              onClick={() => runAction(currentStep.action || "next")}
            >
              {currentStep.cta}
            </Button>
            {currentStep.skip && (
              <Button
                variant="ghost"
                className="text-muted-foreground hover:text-white"
                onClick={skip}
              >
                {currentStep.skip}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Spotlight step ───────────────────────────────────────────────────────
  const hasRect = targetRect && targetRect.width > 0;
  const sl = hasRect ? targetRect.left - PAD : vpSize.w / 2 - 160;
  const st = hasRect ? targetRect.top - PAD : vpSize.h / 2 - 30;
  const sw = hasRect ? targetRect.width + PAD * 2 : 320;
  const sh = hasRect ? targetRect.height + PAD * 2 : 60;

  const TOOLTIP_W = 320;
  const OFFSET = 14;
  const pos = currentStep.pos || "bottom";

  // Compute tooltip position
  let tipLeft, tipTop;
  if (pos === "bottom") {
    tipLeft = Math.max(16, Math.min(sl, vpSize.w - TOOLTIP_W - 16));
    tipTop = st + sh + OFFSET;
  } else if (pos === "top") {
    tipLeft = Math.max(16, Math.min(sl, vpSize.w - TOOLTIP_W - 16));
    tipTop = Math.max(80, st - OFFSET - 210);
  } else {
    // right
    tipLeft = Math.min(sl + sw + OFFSET, vpSize.w - TOOLTIP_W - 16);
    tipTop = Math.max(80, Math.min(st - 20, vpSize.h - 240));
  }
  if (tipTop + 240 > vpSize.h) tipTop = Math.max(16, vpSize.h - 260);
  if (tipLeft < 16) tipLeft = 16;

  return (
    <>
      {/* Spotlight ring — box-shadow creates the darkened surround */}
      <div
        className="fixed pointer-events-none z-[9990] rounded-lg"
        style={{
          left: sl,
          top: st,
          width: sw,
          height: sh,
          transition: "left 0.3s ease, top 0.3s ease, width 0.3s ease, height 0.3s ease",
          boxShadow: hasRect
            ? "0 0 0 9999px rgba(0,0,0,0.72), 0 0 0 2px #00E5FF, 0 0 16px 2px rgba(0,229,255,0.25)"
            : "none",
          borderRadius: 8,
        }}
      />

      {/* Fallback dim when element not found yet */}
      {!hasRect && (
        <div className="fixed inset-0 z-[9990] bg-black/65 pointer-events-none" />
      )}

      {/* Tooltip panel */}
      <div
        className="fixed z-[9999]"
        style={{
          left: tipLeft,
          top: tipTop,
          width: TOOLTIP_W,
          maxWidth: "calc(100vw - 32px)",
          animation: "tutFadeUp 0.2s ease both",
        }}
      >
        <div className="bg-[#0F1015] border border-primary/30 rounded-xl p-5 shadow-2xl">
          {/* Header row */}
          <div className="flex items-center gap-2 mb-3">
            {currentStep.badge && (
              <span className="text-[10px] font-bold uppercase tracking-widest text-primary bg-primary/10 px-2.5 py-1 rounded-full whitespace-nowrap">
                {currentStep.badge}
              </span>
            )}
            <button
              onClick={skip}
              className="ml-auto text-muted-foreground hover:text-white transition-colors p-0.5 shrink-0"
              aria-label="Close tutorial"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <h3 className="font-heading font-black text-[15px] tracking-tight mb-2 leading-snug">
            {currentStep.title}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">{currentStep.desc}</p>

          <Button
            className={`w-full h-9 font-bold text-sm gap-1.5 ${
              currentStep.highlight
                ? "bg-primary text-black hover:bg-primary/90 shadow-lg shadow-primary/20"
                : "bg-white/10 hover:bg-white/15 text-white border-0"
            }`}
            onClick={() => runAction(currentStep.action)}
          >
            {currentStep.cta}
            {!currentStep.action && <ChevronRight className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {/* Skip link */}
      <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[9999]">
        <button
          onClick={skip}
          className="text-xs text-white/40 hover:text-white/70 transition-colors px-3 py-1.5 bg-black/50 rounded-full backdrop-blur-sm"
        >
          Skip tutorial
        </button>
      </div>

      <style>{`
        @keyframes tutFadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
